import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { attrsHaveChangeBy, attrsHaveChangeBySameUser } from '@common/prosemirror/changeset/tracked-changes';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { attrsHaveChange, changeNodeAttrsAreEqual } from '@common/prosemirror/tracked-changes/compare';
import { TrackedChangeNodeAttrs } from '@common/prosemirror/tracked-changes/tracked-change-node-attrs.type';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { getTrackedChangeIdsFromAttrValue, getTrackedChangeIdsNodeAttr, getTrackedChangeNodeAttrs, makeTrackedChangeIdsAttrValue, makeTrackedChangeNodeAttrsFromChangeList } from '@common/prosemirror/tracked-changes/tracked-changes';
import { clone, cloneDeep } from 'lodash';
import { Fragment, MarkType, ProseMirrorNode, Slice } from 'prosemirror-model';

// Returns a new attrs object with the changes merged together
function mergeChanges(attrs: TrackedChangeNodeAttrs, changes: TrackedChange | TrackedChange[]): TrackedChangeNodeAttrs {
  if (!Array.isArray(changes)) {
    changes = [changes];
  }

  const changeIds = getTrackedChangeIdsFromAttrValue(getTrackedChangeIdsNodeAttr(attrs) ?? '');
  const changeList: TrackedChange[] = clone(attrs.changeList ?? []);

  changes.forEach(change => {
    changeIds.push(change.id);
    changeList.push(change);
  });

  return {
    [ChangeAttrName.ChangeIds]: makeTrackedChangeIdsAttrValue(changeIds),
    changeList: changeList
  };
}

// Returns a new attrs object with the changes removed
export function removeChanges(attrs: TrackedChangeNodeAttrs, changeTypesToRemove: ChangeType | ChangeType[]): TrackedChangeNodeAttrs {
  if (!Array.isArray(changeTypesToRemove)) {
    changeTypesToRemove = [changeTypesToRemove];
  }

  let changeIds = getTrackedChangeIdsFromAttrValue(getTrackedChangeIdsNodeAttr(attrs) ?? '');
  let changeList: TrackedChange[] = clone(attrs.changeList ?? []);

  changeList = changeList.filter(change => {
    if ((changeTypesToRemove as ChangeType[]).includes(change.changeType)) {
      changeIds = changeIds.filter(changeId => changeId !== change.id);
      return false;
    } else {
      return true;
    }
  });

  return {
    [ChangeAttrName.ChangeIds]: makeTrackedChangeIdsAttrValue(changeIds),
    changeList: changeList
  };
}

/**
 * Replaces tracked changes within TrackedChangeNodeAttrs.
 * @param attrs The attributes to replace the changes within.
 * @param iteratee A callback function that is called for each change and returns a new change to replace it or nothing to keep the original change.
 * @returns The new TrackedChangeNodeAttrs with the replaced changes.
 */
export function replaceChangesBy(attrs: TrackedChangeNodeAttrs, iteratee: (change: TrackedChange) => TrackedChange): TrackedChangeNodeAttrs {
  const changeList: TrackedChange[] = attrs?.changeList ?? [];

  return makeTrackedChangeNodeAttrsFromChangeList(changeList.map(change => {
    return iteratee(change) ?? change;
  }));
}

/**
 * Applies a tracked change to an existing set of change node attributes.
 * @param existingAttrs The existing attributes to apply the new change to
 * @param trackedChange The change to apply to the existing attributes
 * @return The new attributes with the change applied to them
 */
export function applyChangeToNodeAttrs(existingAttrs: TrackedChangeNodeAttrs, trackedChange: TrackedChange): TrackedChangeNodeAttrs {
  const changeType = trackedChange.changeType;

  // Mixing adds and removes from the same user just leads to no changes
  if ((changeType === ChangeType.Remove && attrsHaveChangeBySameUser(existingAttrs, ChangeType.Add, trackedChange)) ||
    (changeType === ChangeType.Add && attrsHaveChangeBySameUser(existingAttrs, ChangeType.Remove, trackedChange))) {
    // Delete the attrs
    return null;
    // Mixing removes just keeps the original remove
  } else if (changeType === ChangeType.Remove && attrsHaveChange(existingAttrs, ChangeType.Remove)) {
    // Keep the attrs as is
    return existingAttrs;
    // Mixing adds just gives the node the new add
  } else if (changeType === ChangeType.Add && attrsHaveChange(existingAttrs, ChangeType.Add)) {
    // Use the new attrs
    return makeTrackedChangeNodeAttrsFromChangeList([trackedChange]);
    // Mixing replaces substitutes the old replace with the new replace
  } else if (changeType === ChangeType.Replace && attrsHaveChange(existingAttrs, ChangeType.Replace)) {
    // Remove the old replace and merge in the new replace
    return mergeChanges(removeChanges(existingAttrs, ChangeType.Replace), trackedChange);
    // Mixing attribute changes (of the same name) keeps the old change but updates the user and timestamp info
  } else if (changeType === ChangeType.Attributes && attrsHaveChangeBy(existingAttrs, change => change.changeType === ChangeType.Attributes && change.attribute === trackedChange.attribute)) {
    // Replace the existing attribute change with the new change but keep the original value
    return replaceChangesBy(existingAttrs, change => {
      if (change.changeType === ChangeType.Attributes && change.attribute === trackedChange.attribute) {
        return {
          ...trackedChange,
          value: change.value // Keep the original value
        };
      }
    });
    // Mixing anything but an add or remove with an existing add just keeps the original add
  } else if (changeType !== ChangeType.Add && changeType !== ChangeType.Remove && attrsHaveChange(existingAttrs, ChangeType.Add)) {
    // Keep the node as is
    return existingAttrs;
    // Mixing any changes that do not include an add just merges the changes
  } else if (changeType !== ChangeType.Add && existingAttrs && !attrsHaveChangeBySameUser(existingAttrs, ChangeType.Add, trackedChange)) {
    // Merge the changes
    return mergeChanges(existingAttrs, trackedChange);
    // If the node does not have a change then just give the node the new change
  } else {
    // Use the new attrs
    return makeTrackedChangeNodeAttrsFromChangeList([trackedChange]);
  }
}

/*
 * copyNodeBySettingChanges
 * Copies a node overriding any changes with the changes from `options`
 */
export function copyNodeBySettingChanges(changeMarkType: MarkType, node: ProseMirrorNode, attrs: TrackedChangeNodeAttrs): ProseMirrorNode {
  if (node.isText || node.type.spec.unchangeable) {
    return node.mark(changeMarkType.create({ ...attrs }).addToSet(node.marks));
  } else {
    return node.type.create(Object.assign({}, node.attrs, attrs), node.content, node.marks);
  }
}

/**
 * Creates a new ProseMirror node with the given change applied to it.
 * Uses the applyChange function to apply the changes.
 * If the new node is the same as the original node then the original node is returned.
 * @param changeMarkType The type of change being applied to the node.
 * @param node The node to copy and apply the changes to.
 * @param attrs The change being applied to the node.
 * @return The new node with the change applied to it. If the new node is the same as the original node then the original node is returned.
 */
export function copyNodeByApplyingChanges(changeMarkType: MarkType, node: ProseMirrorNode, attrs: TrackedChangeNodeAttrs, copyState?: CopyState): ProseMirrorNode {
  if (node.type.spec.skipTrackChanges) {
    return node;
  }

  const newChangeNodeAttrs = applyChangeToNodeAttrs(getTrackedChangeNodeAttrs(changeMarkType, node) ?? (node.isText ? copyState?.parentTrackedChangeAttrs : undefined), attrs.changeList[0]);

  if (newChangeNodeAttrs) {
    // Ignore the change since the parent will have the change
    if (copyState?.isParentTextBlock && node.isText) {
      return node.mark([]).copy(node.content);
    } else if (changeNodeAttrsAreEqual(node.attrs, newChangeNodeAttrs)) {
      return node;
    } else {
      return copyNodeBySettingChanges(changeMarkType, node, newChangeNodeAttrs);
    }
  } else if (node.isText) {
    // Tracked added text is deleted. Return a empty text node
    return node.mark([]).copy();
  } else {
    return null;
  }
}

/**
 * Tracks the state of copying a slice, fragment, or nodes with changes.
 */
interface CopyState {
  /**
   * The current depth into the slice/fragment/node being copied.
   */
  depth: number;
  /**
   * Whether or not the copying action is currently in the first branch of the node tree.
   */
  inFirstBranch: boolean;
  /**
   * Whether or not the copying action is currently in the last branch of the node tree.
   */
  inLastBranch: boolean;
  /**
   * Whether or not the parent node is a text block. When it is, don't apply changes to text nodes.
   */
  isParentTextBlock?: boolean;
  /**
   * The parent node's tracked changed attrs
   */
  parentTrackedChangeAttrs?: TrackedChangeNodeAttrs;
}

/*
 * copyNodeWithChanges
 * Copies a node with tracked changes applied to it
 */
export function copyNodeWithChanges(changeMarkType: MarkType, node: ProseMirrorNode, attrs: TrackedChangeNodeAttrs, openStart: number = 0, openEnd: number = 0, copyState: CopyState = { depth: 0, inFirstBranch: true, inLastBranch: true, isParentTextBlock: false }): ProseMirrorNode {
  let newNode = node;

  // Increment the depth now that this node has been entered
  copyState.depth += 1;

  // Do not apply changes to this node if the slice it belongs to is open to this node
  const applyTrackedChanges = !((copyState.inFirstBranch && openStart >= copyState.depth) || (copyState.inLastBranch && openEnd >= copyState.depth));

  // If the node is not a leaf and has changeable content, apply changes to the node and it's children
  if (!node.isLeaf && !node.type.spec.unchangeable && node.content.size !== 0) {
    const copyStateClone = cloneDeep(copyState);
    // Tells the text nodes that the changes will instead be on the parent
    copyStateClone.isParentTextBlock = applyTrackedChanges;

    // Update attrs with the parent's attrs
    if (!copyStateClone.parentTrackedChangeAttrs) {
      const parentTrackedChangeAttrs = getTrackedChangeNodeAttrs(changeMarkType, node);
      copyStateClone.parentTrackedChangeAttrs = parentTrackedChangeAttrs ?? ((openStart === 0 && openEnd === 0) ? attrs : null);
    }

    // Apply the change to the node's content
    let fragment = copyFragmentWithChanges(changeMarkType, node.content, attrs, openStart, openEnd, copyStateClone);

    newNode = node.copy(fragment);
    if (applyTrackedChanges) {
      newNode = copyNodeByApplyingChanges(changeMarkType, newNode, attrs, copyStateClone);
    }
  } else {
    if (applyTrackedChanges) {
      newNode = copyNodeByApplyingChanges(changeMarkType, node, attrs, copyState);
    }
  }

  // Decrement the depth now that this node is being exited
  copyState.depth -= 1;

  return newNode;
}

/*
 * copyFragmentWithChanges
 * Copies a fragment with tracked changes applied to it
 */
export function copyFragmentWithChanges(changeMarkType: MarkType, fragment: Fragment, attrs: TrackedChangeNodeAttrs, openStart: number = 0, openEnd: number = 0, copyState: CopyState = { depth: 0, inFirstBranch: true, inLastBranch: true, isParentTextBlock: false }): Fragment {
  const newNodes: ProseMirrorNode[] = [];

  fragment.forEach((node, offset, index) => {
    let newNode;

    // If this is the only child
    if (index === 0 && index === fragment.childCount - 1) {
      newNode = copyNodeWithChanges(changeMarkType, node, attrs, openStart, openEnd, copyState);
      // Else if this is the first child
    } else if (index === 0) {
      newNode = copyNodeWithChanges(changeMarkType, node, attrs, openStart, openEnd, {
        ...copyState,
        inFirstBranch: copyState.inFirstBranch,
        inLastBranch: false
      });
      // Else if this is the last child
    } else if (index === fragment.childCount - 1) {
      newNode = copyNodeWithChanges(changeMarkType, node, attrs, openStart, openEnd, {
        ...copyState,
        inFirstBranch: false,
        inLastBranch: copyState.inLastBranch
      });
      // Else this is a middle child
    } else {
      newNode = copyNodeWithChanges(changeMarkType, node, attrs, openStart, openEnd, {
        ...copyState,
        inFirstBranch: false,
        inLastBranch: false
      });
    }
    // Check the node exists and isn't an empty text node
    if (newNode && !(newNode.isText && newNode.textContent === '')) {
      newNodes.push(newNode);
    }
  });

  return Fragment.fromArray(newNodes.length ? newNodes : []);
}

/**
 * Copies a slice with tracked changes applied to it.
 */
export function copySliceWithChanges(changeMarkType: MarkType, slice: Slice, attrs: TrackedChangeNodeAttrs): Slice {
  // Create a new slice by making a copy of the slice's fragment with changes added
  const fragment = copyFragmentWithChanges(changeMarkType, slice.content, attrs, slice.openStart, slice.openEnd);

  if (fragment.size === 0 || fragment.size < slice.openStart + slice.openEnd) {
    return Slice.empty;
  } else {
    return new Slice(fragment, slice.openStart, slice.openEnd);
  }
}
